import { useEffect, useRef, useState } from 'react';

/**
 * Mindest-Scrollweg in Pixeln, ab dem eine Richtung als gewollt gilt.
 * Verhindert, dass der Header bei Daumen-Zittern zappelt.
 */
export const SCROLL_DELTA_THRESHOLD = 8;

/**
 * Ruhezone am Seitenanfang: In den obersten N Pixeln ist immer sichtbar.
 * Sonst würde der Header schon beim ersten Antippen verschwinden und beim
 * Zurückkommen nach oben unzuverlässig wieder auftauchen.
 */
export const TOP_ZONE_PX = 80;

/**
 * Blendet ein Element beim Runterscrollen aus und beim Hochwischen wieder ein
 * (Verhalten der Chrome-Adressleiste auf Mobile).
 *
 * @param {object}  [options]
 * @param {boolean} [options.enabled=true]  false → immer sichtbar, kein Listener aktiv
 * @param {number}  [options.threshold]     Mindest-Scrollweg pro Richtungsentscheid
 * @param {number}  [options.topZone]       Höhe der Ruhezone am Seitenanfang
 * @param {*}       [options.resetKey]      Wechselt dieser Wert, wird wieder eingeblendet
 *                                          (z. B. der Pfad bei einem Seitenwechsel)
 * @returns {boolean} true = soll versteckt sein
 */
export default function useAutoHideOnScroll({
  enabled = true,
  threshold = SCROLL_DELTA_THRESHOLD,
  topZone = TOP_ZONE_PX,
  resetKey,
} = {}) {
  const [isHidden, setIsHidden] = useState(false);
  const lastYRef = useRef(0);
  const frameRef = useRef(null);
  const geplantRef = useRef(false);

  useEffect(() => {
    // Jeder Wechsel von enabled/resetKey (Seitenwechsel, Menü) blendet ein.
    setIsHidden(false);
    lastYRef.current = window.scrollY;

    if (!enabled) return undefined;

    const evaluate = () => {
      const y = window.scrollY;
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;

      // Gummiband (iOS): über die Kanten hinaus nichts entscheiden.
      if (y < 0 || (maxScroll > 0 && y > maxScroll)) {
        lastYRef.current = y;
        return;
      }

      if (y <= topZone) {
        setIsHidden(false);
        lastYRef.current = y;
        return;
      }

      const delta = y - lastYRef.current;
      // Referenzpunkt absichtlich NICHT nachziehen: kleine Bewegungen sollen
      // sich aufsummieren, bis die Schwelle erreicht ist.
      if (Math.abs(delta) < threshold) return;

      setIsHidden(delta > 0);
      lastYRef.current = y;
    };

    const handleScroll = () => {
      if (geplantRef.current) return;
      // Wenn requestAnimationFrame synchron ausführt, ist der Callback schon
      // durch, bevor die Frame-ID zugewiesen wird — die ID taugt deshalb nicht
      // als „schon eingeplant"-Merker; das Flag wird darum VOR dem Scheduling gesetzt.
      geplantRef.current = true;
      frameRef.current = window.requestAnimationFrame(() => {
        geplantRef.current = false;
        frameRef.current = null;
        evaluate();
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (geplantRef.current && frameRef.current != null) {
        window.cancelAnimationFrame(frameRef.current);
      }
      geplantRef.current = false;
    };
  }, [enabled, threshold, topZone, resetKey]);

  return isHidden;
}
